import type { Valuation } from "./valuation.js";

/**
 * The six recurrence intervals, and the exact ratio each scales by to reach a monthly equivalent.
 *
 * ==========================================================================================
 * WHY THE RATIOS ARE HERE AND THE CLASS IS NOT
 * ==========================================================================================
 *
 * Feature 009 wrote these for expenses. Feature 015 needs the same six for income, and a domain
 * package may not import another (Principle IV) — so the table either gets copied or promoted, and
 * copying is the option that can go wrong quietly.
 *
 * It would go wrong in a specific way worth naming. The dashboard's household-health card (013)
 * subtracts an income monthly equivalent from an expense one. Two tables that had drifted by a
 * single ratio would produce a surplus that is wrong, on a page where both halves look reasonable
 * and neither can be checked against the other. A test can assert the two agree; a shared table makes
 * the assertion true by construction.
 *
 * What is NOT here is the `RecurrenceInterval` class. Each context keeps its own, because each
 * returns its own `ErrorCode` for an unrecognised value — `EXP_INTERVAL_UNKNOWN`,
 * `INC_INTERVAL_UNKNOWN` — which reaches the HTTP mapper and the API client, and which is the
 * taxonomy Principle V asks for. The same split as `iso-day.ts`, for the same reason: what is shared
 * is what can drift silently, and a wrong error code cannot.
 *
 * ==========================================================================================
 * WHY numerator/denominator AND NOT A MULTIPLIER
 * ==========================================================================================
 *
 * `52 / 12` is 4.333… and has no exact representation as a decimal multiplier. Held as two integers
 * it feeds `Valuation.scaledBy`, which multiplies exactly in `bigint` and divides once. A `4.3333`
 * constant would round twice and produce €433.33 or €433.34 depending on where.
 */

export const RECURRENCE_INTERVALS = [
  "weekly",
  "every_4_weeks",
  "monthly",
  "quarterly",
  "half_yearly",
  "yearly",
] as const;

export type RecurrenceIntervalValue = (typeof RECURRENCE_INTERVALS)[number];

export interface MonthlyRatio {
  readonly numerator: bigint;
  readonly denominator: bigint;
}

/** The exact ratio each interval scales by to reach a monthly equivalent. */
export const MONTHLY_RATIOS: Record<RecurrenceIntervalValue, MonthlyRatio> = {
  // 52 weeks a year, over 12 months. NOT `× 4` — four weeks is not a month, and the difference over
  // a year is a whole extra week of the amount.
  weekly: { numerator: 52n, denominator: 12n },
  // 13 four-week periods a year. The interval a lot of Dutch direct debits and salaries actually use.
  every_4_weeks: { numerator: 13n, denominator: 12n },
  monthly: { numerator: 1n, denominator: 1n },
  quarterly: { numerator: 1n, denominator: 3n },
  half_yearly: { numerator: 1n, denominator: 6n },
  yearly: { numerator: 1n, denominator: 12n },
};

export function isRecurrenceIntervalValue(input: string): input is RecurrenceIntervalValue {
  return (RECURRENCE_INTERVALS as readonly string[]).includes(input);
}

/**
 * What one recurring amount comes to per month — THE one implementation, for money in and money out.
 *
 * ONE ROUNDING, AND IT HAPPENS HERE. `Valuation.scaledBy` multiplies exactly in `bigint` and divides
 * once, half away from zero. Every total downstream is `Valuation.plus` over these ALREADY-ROUNDED
 * figures, which is what makes a displayed total equal the sum of the displayed rows and what makes a
 * settle-up line reproducible by hand from the numbers printed beside it.
 *
 * The cost of that choice, stated rather than discovered later: a household total is not the exactly
 * correct sum of the exact monthly equivalents. With twenty weekly items the accumulated rounding can
 * reach a few cents. That is accepted deliberately — a total nobody can verify by adding up what they
 * see is worse than a total a few cents from an ideal nobody can see.
 */
export function monthlyEquivalentOf(
  amount: Valuation,
  interval: RecurrenceIntervalValue,
): Valuation {
  const { numerator, denominator } = MONTHLY_RATIOS[interval];
  return amount.scaledBy(numerator, denominator);
}
